import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { LoadingOutlined, WarningOutlined } from '@ant-design/icons';
import {
  GoogleMap,
  InfoWindow,
  Marker,
  MarkerClustererF,
  Polyline,
  useLoadScript,
} from '@react-google-maps/api';
import { Card, Segmented } from 'antd';
import { useTranslation } from 'react-i18next';

import { GOOGLE_MAP_API_KEY } from 'config/Config';

import {
  MapBounds,
  MapPoint,
  PinState,
  RouteExecution,
  computeBounds,
  executionMatchesSelection,
  visiblePoints,
  withoutLocationCount,
} from '../utils/mapData';
import { CLUSTER_STYLES, pinIcon } from '../utils/mapIcons';
import { VisitSelection } from '../utils/selection';
import { stateColorVars } from '../utils/stateColors';
import VisitPinInfo from './VisitPinInfo';

const I18N_BASE = 'scenes.visit_history.map';

type PathOrder = 'executed' | 'planned';

/** Clusters exist at zoom 11 and below; from 12 every pin is drawn. */
const CLUSTER_MAX_ZOOM = 11;
const FIT_PADDING = 48;
const SINGLE_POINT_ZOOM = 15;
const DIMMED_OPACITY = 0.45;
const LINE_OPACITY = 0.8;
const LINE_DIMMED_OPACITY = 0.3;
/** Only used until the first bounds are fitted, and when nothing is visible. */
const FALLBACK_CENTER = { lat: -33.4489, lng: -70.6693 };
const FALLBACK_ZOOM = 10;
const DASH = { path: 'M 0,-1 0,1', scale: 3 };

// Incidents on top of executed pins, pending ones below. Matching the
// selection adds more than the whole state range, so a dimmed pin never covers
// a matching one; the state order only breaks ties among equals.
const Z_BY_STATE: Record<PinState, number> = {
  incident: 3000,
  executed: 2000,
  pending: 1000,
};
const Z_MATCH_BONUS = 10000;

const fitTo = (map: google.maps.Map, bounds: MapBounds) => {
  if (bounds.north === bounds.south && bounds.east === bounds.west) {
    map.setCenter({ lat: bounds.north, lng: bounds.east });
    map.setZoom(SINGLE_POINT_ZOOM);

    return;
  }
  map.fitBounds(bounds, FIT_PADDING);
};

interface PinsProps {
  executions: RouteExecution[];
  visibleKeys: Set<string>;
  selection: VisitSelection;
  clusterer: any;
  onPick: (executionKey: string, point: MapPoint) => void;
}

/** Memoized: opening an InfoWindow must not re-render a thousand markers. */
const Pins = memo(function Pins({
  executions,
  visibleKeys,
  selection,
  clusterer,
  onPick,
}: PinsProps) {
  return (
    <>
      {executions
        .filter((e) => visibleKeys.has(e.key))
        .flatMap((execution) => {
          const dimmed = !executionMatchesSelection(execution, selection);

          return execution.plannedOrder.map((point) => (
            <Marker
              key={point.stop.stopId}
              position={{ lat: point.lat, lng: point.lng }}
              icon={pinIcon(execution.color, point.state, point.number)}
              opacity={dimmed ? DIMMED_OPACITY : 1}
              zIndex={Z_BY_STATE[point.state] + (dimmed ? 0 : Z_MATCH_BONUS)}
              clusterer={clusterer}
              onClick={() => onPick(execution.key, point)}
            />
          ));
        })}
    </>
  );
});

interface RouteLinesProps {
  executions: RouteExecution[];
  visibleKeys: Set<string>;
  selection: VisitSelection;
  order: PathOrder;
}

const RouteLines = memo(function RouteLines({
  executions,
  visibleKeys,
  selection,
  order,
}: RouteLinesProps) {
  return (
    <>
      {executions
        .filter((e) => visibleKeys.has(e.key))
        .map((execution) => {
          const points =
            order === 'planned'
              ? execution.plannedOrder
              : execution.executedOrder;
          if (points.length < 2) return null;

          const opacity = executionMatchesSelection(execution, selection)
            ? LINE_OPACITY
            : LINE_DIMMED_OPACITY;
          // Planned order is dashed: a hidden line repeated with a dash symbol.
          const options =
            order === 'planned'
              ? {
                  strokeColor: execution.color,
                  strokeOpacity: 0,
                  icons: [
                    {
                      icon: {
                        ...DASH,
                        strokeColor: execution.color,
                        strokeOpacity: opacity,
                        strokeWeight: 3,
                      },
                      offset: '0',
                      repeat: '12px',
                    },
                  ],
                }
              : {
                  strokeColor: execution.color,
                  strokeOpacity: opacity,
                  strokeWeight: 3,
                };

          return (
            <Polyline
              key={execution.key}
              path={points.map((p) => ({ lat: p.lat, lng: p.lng }))}
              options={options}
            />
          );
        })}
    </>
  );
});

interface VisitMapProps {
  executions: RouteExecution[];
  visibleKeys: Set<string>;
  selection: VisitSelection;
  timeZone: string;
}

/**
 * The visits map: one pin per stop with a location (numbered with its planned
 * sequence) and one line per visible route execution. The pins and lines
 * follow the shared selection, dimming what it leaves out; a selected route
 * is also the one the map frames.
 */
function VisitMap({
  executions,
  visibleKeys,
  selection,
  timeZone,
}: VisitMapProps) {
  const { t } = useTranslation();
  // The same options as every other map of the app: a different set makes the
  // Maps loader throw at runtime.
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAP_API_KEY,
  });

  const [order, setOrder] = useState<PathOrder>('executed');
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [active, setActive] = useState<{ key: string; point: MapPoint } | null>(
    null
  );

  const focused = useMemo(
    () =>
      selection?.kind === 'route'
        ? executions.find(
            (e) =>
              visibleKeys.has(e.key) &&
              executionMatchesSelection(e, selection) &&
              e.plannedOrder.length > 0
          )
        : undefined,
    [executions, visibleKeys, selection]
  );

  const targetBounds = useMemo(
    () =>
      computeBounds(
        focused ? focused.plannedOrder : visiblePoints(executions, visibleKeys)
      ),
    [focused, executions, visibleKeys]
  );

  // A stable string of what the map frames: the visible route keys and the
  // bounds values. A refresh with identical data builds new objects but the
  // same key, and a selection that only dims (a van, a day) leaves it
  // unchanged too: neither may move a map the user panned or zoomed.
  const fitKey = useMemo(
    () =>
      targetBounds
        ? [
            [...visibleKeys].sort().join(','),
            targetBounds.north,
            targetBounds.south,
            targetBounds.east,
            targetBounds.west,
          ].join('|')
        : null,
    [targetBounds, visibleKeys]
  );
  const lastFit = useRef<{ map: google.maps.Map; key: string } | null>(null);
  useEffect(() => {
    if (!map || !targetBounds || fitKey === null) return;
    if (lastFit.current?.map === map && lastFit.current.key === fitKey) return;

    lastFit.current = { map, key: fitKey };
    fitTo(map, targetBounds);
  }, [map, fitKey, targetBounds]);

  const onPick = useCallback(
    (key: string, point: MapPoint) => setActive({ key, point }),
    []
  );

  const withoutLocation = withoutLocationCount(executions, visibleKeys);
  const visibleRoutes = executions.filter((e) => visibleKeys.has(e.key));
  const activeExecution = active
    ? visibleRoutes.find((e) => e.key === active.key)
    : undefined;

  const legend = (
    <div className="visit-history-map__legend" style={stateColorVars}>
      <span>
        <i className="visit-history-map__swatch visit-history-map__swatch--executed" />
        {t(`${I18N_BASE}.legend.executed`)}
      </span>
      <span>
        <i className="visit-history-map__swatch visit-history-map__swatch--incident" />
        {t(`${I18N_BASE}.legend.incident`)}
      </span>
      <span>
        <i className="visit-history-map__swatch visit-history-map__swatch--pending" />
        {t(`${I18N_BASE}.legend.pending`)}
      </span>
    </div>
  );

  const toggle = (
    <Segmented
      value={order}
      onChange={(value) => setOrder(value as PathOrder)}
      aria-label={t(`${I18N_BASE}.order.label`)}
      options={[
        { value: 'executed', label: t(`${I18N_BASE}.order.executed`) },
        { value: 'planned', label: t(`${I18N_BASE}.order.planned`) },
      ]}
    />
  );

  const body = loadError ? (
    <div className="visit-history-map__state visit-history-map__state--error">
      <WarningOutlined />
      <span>{t(`${I18N_BASE}.load_error`)}</span>
    </div>
  ) : !isLoaded ? (
    <div className="visit-history-map__state">
      <LoadingOutlined />
      <span>{t(`${I18N_BASE}.loading`)}</span>
    </div>
  ) : (
    <div className="visit-history-map__canvas">
      <GoogleMap
        mapContainerClassName="visit-history-map__gmap"
        center={FALLBACK_CENTER}
        zoom={FALLBACK_ZOOM}
        onLoad={setMap}
        onUnmount={() => setMap(null)}
        onClick={() => setActive(null)}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
        }}
      >
        <MarkerClustererF
          options={{
            maxZoom: CLUSTER_MAX_ZOOM,
            styles: CLUSTER_STYLES,
            averageCenter: true,
          }}
        >
          {(clusterer) => (
            <Pins
              executions={executions}
              visibleKeys={visibleKeys}
              selection={selection}
              clusterer={clusterer}
              onPick={onPick}
            />
          )}
        </MarkerClustererF>

        <RouteLines
          executions={executions}
          visibleKeys={visibleKeys}
          selection={selection}
          order={order}
        />

        {active && activeExecution && (
          <InfoWindow
            position={{ lat: active.point.lat, lng: active.point.lng }}
            onCloseClick={() => setActive(null)}
          >
            <VisitPinInfo
              point={active.point}
              execution={activeExecution}
              timeZone={timeZone}
            />
          </InfoWindow>
        )}
      </GoogleMap>
      {targetBounds === null && (
        <div className="visit-history-map__empty">
          {t(`${I18N_BASE}.no_points`)}
        </div>
      )}
    </div>
  );

  return (
    <Card
      className="visit-history-map"
      title={t(`${I18N_BASE}.title`)}
      extra={toggle}
      styles={{ body: { padding: 0 } }}
    >
      <div className="visit-history-map__meta">
        {t(`${I18N_BASE}.meta.no_location`, { count: withoutLocation })}
        {' · '}
        {t(`${I18N_BASE}.meta.routes_visible`, { count: visibleRoutes.length })}
      </div>
      {body}
      {legend}
    </Card>
  );
}

export default VisitMap;
